/**
 * The Connect tab's Embed card — the snippet, and who may answer it.
 *
 * Visual Reference: `design/screens/editor-connect.html`, the **Embed** card
 * and the **Domain allowlist** field. The `cdn.leadgible.app` host in the mockup
 * is a placeholder — the real one comes from the org's plan (RULE-022) and the
 * server sends it.
 *
 * TWO MODES, NOT FIVE. The export draws Inline · Popup · Slide-in · Full page ·
 * Hosted page, and DECISION-021 cut three: "three display modes — popup,
 * slide-in, full-page takeover — leaving inline and hosted page". The same
 * reason the block palette shows 17 of 24.
 *
 * THREE CARDS, THREE MODULES, ONE TAB. The embed card is about who may ANSWER
 * the funnel (6.7); `Routing` is what happens once somebody has (9.1); and
 * `DeliveryLog` is what actually went out (9.5). They share a tab and nothing
 * else — each fetches its own and holds its own states, so none can put another
 * into a loading state or take it down with an error.
 *
 * Stories 6.7, 9.1 and 9.5.
 */
import { useCallback, useEffect, useState } from 'react'
import { ApiFailure, apiGet, apiPost, apiPut } from '../lib/api'
import { useShell } from '../shell/workspace'
import { Button } from '../ui/Button'
import { Callout } from '../ui/Callout'
import { Skeleton } from '../ui/Skeleton'
import { COMPOSED, DRAWN } from './connect-copy'
import { DeliveryLog } from './DeliveryLog'
import { Routing } from './Routing'
import { useEditor } from './store'

type EmbedMode = 'inline' | 'hosted'

type EmbedView = {
  funnelId: string
  snippets: Record<EmbedMode, string>
  domainAllowlist: string[]
  scriptSrc: string
  apiOrigin: string
  status: string
  name: string
}

const MODE_LABEL: Record<EmbedMode, string> = { inline: 'Inline', hosted: 'Hosted page' }

/**
 * Whether the Connect tab offers the hosted page — OPEN AGAIN since Story 25.9.
 *
 * DECISION-160 withdrew it on 2026-09-23: `GET /f/{fid}` rendered, and every
 * submission from it was refused, because the runtime posted from the page's
 * own origin and no allowlist named it (Q-126, measured on staging). Story 25.9
 * moved the page to the plan's embed host and made it take its own submissions
 * there, proved end to end by `e2e/hosted-page.spec.ts`, and its last criterion
 * sets this back to true.
 *
 * IT STAYS A SWITCH, not a deleted branch: if the hosted page ever cannot take a
 * submission again, one line withdraws it — hidden, never disabled, since a
 * control in `packages/app` does something or is gone — and the docs page's
 * `HOSTED_PAUSED` follows it, because `tests/site-content.test.ts` holds the two
 * together.
 */
const HOSTED_OPEN: boolean = true

/** What the mode strip offers — two of the export's five (DECISION-021). */
const MODES: readonly EmbedMode[] = HOSTED_OPEN ? ['inline', 'hosted'] : ['inline']

/** The drawing's promise about who can submit (Story 25.9) — `connect-copy.ts`. */
export const ALLOWLIST_HELP = DRAWN.allowlistHelp

export function ConnectTab({ funnelId, status }: { funnelId: string; status: string }) {
  return (
    <Frame>
      <EmbedCard funnelId={funnelId} status={status} />
      <Routing funnelId={funnelId} />
      {/*
        Story 9.5. Below routing, as the export draws it: configure what
        happens, then read what did. The log is WORKSPACE-wide rather than
        per-funnel — `deliveries` has no funnel column, and "did my sends
        arrive" is a question about the integration rather than about one
        funnel, which is why `integrations.html` draws the same card too.
      */}
      <DeliveryLog />
    </Frame>
  )
}

/* ── the embed card ─────────────────────────────────────────────── */

function EmbedCard({ funnelId, status }: { funnelId: string; status: string }) {
  const { workspace, me, org } = useShell()
  const workspaceId = workspace?.id ?? null
  // Re-reads whenever the draft is saved, so a rename shows in the card.
  const savedAt = useEditor((s) => s.save.status)

  const [view, setView] = useState<EmbedView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<EmbedMode>('inline')
  const [copied, setCopied] = useState(false)

  const load = useCallback(async () => {
    if (!workspaceId) return
    setError(null)
    try {
      setView(await apiGet<EmbedView>(`/v1/w/${workspaceId}/funnels/${funnelId}/embed`))
    } catch (e) {
      setError(
        e instanceof ApiFailure
          ? e.message
          : "We couldn't load the embed code. Check your connection and try again.",
      )
    }
  }, [workspaceId, funnelId])

  // `status` joins `savedAt` as a re-read trigger (Story 16.1). Publishing
  // happens in the chrome, one component up and outside this card's own state,
  // so without it the "not published yet" line below would keep saying so until
  // the tab was left and re-entered — on the one screen whose whole job is to
  // tell somebody whether their snippet works yet.
  useEffect(() => {
    void load()
  }, [load, savedAt, status])

  if (error) return <Callout>{error}</Callout>
  if (!view) {
    return (
      <>
        <Skeleton className="h-mdx-28" style={{ width: '38%' }} />
        <Skeleton className="h-mdx-textarea-min" />
      </>
    )
  }

  const snippet = view.snippets[mode]

  const copy = () => {
    void navigator.clipboard?.writeText(snippet).then(
      () => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 2000)
      },
      () => {
        /* A clipboard a browser refuses is not an error worth a red box — the
           code is on screen and selectable. */
      },
    )
  }

  return (
    <section className="flex flex-col gap-mdx-20 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
      <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">Embed</span>
        <span className="text-mdx-13 text-mdx-slate">
          Paste this once — publishing updates it in place
        </span>
      </div>

      <nav aria-label="Embed mode" className="overflow-x-auto border-b border-mdx-line">
        <div className="flex min-w-max gap-mdx-28">
          {MODES.map((candidate) => (
            <button
              key={candidate}
              type="button"
              aria-current={mode === candidate ? 'true' : undefined}
              onClick={() => setMode(candidate)}
              className={[
                'cursor-pointer whitespace-nowrap border-b-2 bg-transparent px-mdx-4 py-mdx-12 text-mdx-15',
                'transition-colors ease-mdx-swift',
                mode === candidate
                  ? 'border-mdx-brand font-mdx-semibold text-mdx-ink'
                  : 'border-transparent font-mdx-medium text-mdx-slate hover:text-mdx-ink',
              ].join(' ')}
            >
              {MODE_LABEL[candidate]}
            </button>
          ))}
        </div>
      </nav>

      {mode === 'hosted' ? (
        <HostedLink
          link={snippet}
          free={(org ? me.entitlements[org.id]?.plan : undefined) === 'free'}
          published={view.status === 'published'}
          copied={copied}
          onCopy={copy}
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-mdx-control bg-mdx-ground px-mdx-18 py-mdx-16">
            <pre className="m-0 min-w-max whitespace-pre text-mdx-14 leading-mdx-body text-mdx-ink">
              {snippet}
            </pre>
          </div>

          {view.status !== 'published' ? (
            // Not an error — a fact. RULE-015: only a published version is served,
            // so a snippet pasted now renders nothing until Publish is pressed.
            <span className="text-mdx-13 text-mdx-slate">
              This funnel is not published yet, so the code above will not show anything until you
              publish it.
            </span>
          ) : null}

          <div className="flex flex-wrap gap-mdx-12">
            <Button onClick={copy}>{copied ? 'Copied' : 'Copy embed code'}</Button>
            <EmailToDeveloper
              workspaceId={workspaceId}
              funnelId={funnelId}
              mode={mode}
              orgId={me.orgs[0]?.id ?? null}
            />
          </div>
        </>
      )}

      {HOSTED_OPEN ? null : (
        // DECISION-160: why the strip has no Hosted page tab, while it has none.
        <span className="text-mdx-13 text-mdx-slate">
          The hosted page is paused for now: it can't take a submission yet, so every answer would
          be lost at the last step.
        </span>
      )}

      <Allowlist
        workspaceId={workspaceId}
        funnelId={funnelId}
        initial={view.domainAllowlist}
        onSaved={(domains) => setView({ ...view, domainAllowlist: domains })}
      />
    </section>
  )
}

/* ── the hosted page's link · Story 25.9 ────────────────────────────────── */

/**
 * `editor-connect.html`'s `hosted` state: the link on a ground tile with its own
 * Copy, a way to open it, and which host it is on.
 *
 * THE LINK IS THE SERVER'S. `GET …/embed` builds it on the plan's embed host —
 * `leadgible.link` for Free, `cdn.{primary}` for paid (RULE-022, RULE-077) — and
 * this renders what it is handed; nothing here composes a host. The note under
 * it is the one place the plan is read, and only to choose its words: the
 * drawing's paid-plan sentence, or the Free plan's own (composed).
 */
function HostedLink({
  link,
  free,
  published,
  copied,
  onCopy,
}: {
  link: string
  free: boolean
  published: boolean
  copied: boolean
  onCopy: () => void
}) {
  return (
    <div className="flex flex-col gap-mdx-12">
      <p className="m-0 max-w-mdx-measure-60 text-pretty text-mdx-15 text-mdx-slate">{DRAWN.hostedLine}</p>
      <div className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-10 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12">
        {/* `--mdx-sidebar-width`'s 240px, borrowed for its measurement: the basis
            the export gives the link, so Copy wraps under it at 375px. */}
        <span className="min-w-0 flex-1 basis-mdx-sidebar wrap-anywhere text-mdx-15">{link}</span>
        <Button size="small" className="shrink-0" onClick={onCopy}>
          {copied ? 'Copied' : DRAWN.copyLink}
        </Button>
      </div>
      <a
        href={link}
        target="_blank"
        rel="noopener"
        className="self-start text-mdx-15 font-mdx-semibold text-mdx-brand-deep"
      >
        {DRAWN.openPage}
      </a>
      {published ? null : (
        // RULE-015: the page serves published versions only, so until Publish
        // is pressed the link opens a 404 — said here rather than found there.
        <span className="text-mdx-13 text-mdx-slate">{COMPOSED.hostedUnpublished}</span>
      )}
      <span className="max-w-mdx-measure-60 text-pretty text-mdx-13 text-mdx-slate">
        {free ? COMPOSED.hostedFreeNote : DRAWN.hostedPaidNote}
      </span>
    </div>
  )
}

/* ── the allowlist ──────────────────────────────────────────────────────── */

function Allowlist({
  workspaceId,
  funnelId,
  initial,
  onSaved,
}: {
  workspaceId: string | null
  funnelId: string
  initial: string[]
  onSaved: (domains: string[]) => void
}) {
  const [text, setText] = useState(initial.join(', '))
  const [saving, setSaving] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const save = async () => {
    if (!workspaceId) return
    setSaving(true)
    setProblem(null)
    try {
      const result = await apiPut<{ domainAllowlist: string[] }>(
        `/v1/w/${workspaceId}/funnels/${funnelId}/embed`,
        // Split on commas the way the export's single field implies. The server
        // normalises each entry to a hostname and is the only thing that judges.
        { domain_allowlist: text.split(',').map((entry) => entry.trim()).filter(Boolean) },
      )
      setText(result.domainAllowlist.join(', '))
      onSaved(result.domainAllowlist)
    } catch (e) {
      setProblem(
        e instanceof ApiFailure ? e.message : "We couldn't save that. Check your connection.",
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-mdx-6 border-t border-mdx-line-soft pt-mdx-20">
      <label htmlFor="mdx-allowlist" className="text-mdx-14 font-mdx-semibold">
        Domain allowlist
      </label>
      <div className="flex flex-wrap items-center gap-mdx-8">
        <input
          id="mdx-allowlist"
          type="text"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onBlur={() => void save()}
          placeholder={DRAWN.noWebsitePlaceholder}
          className="h-mdx-input min-w-0 flex-1 rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-14 py-mdx-10 text-mdx-15 text-mdx-ink outline-none focus:border-mdx-brand"
        />
        <Button size="small" variant="secondary" disabled={saving} onClick={() => void save()}>
          {saving ? 'Saving' : 'Save'}
        </Button>
      </div>
      {text.trim() === '' ? (
        /*
          `no-website` — a fact, drawn in slate and never the warning colour.
          Until Story 25.9 an empty list answered nobody and this said so in
          review ink; since the hosted page takes its own submissions, an empty
          list refuses every WEBSITE and the funnel still has its own page —
          DECISION-161's no-website path, chosen rather than broken.
        */
        <span className="max-w-mdx-measure text-pretty text-mdx-13 text-mdx-slate">{DRAWN.noWebsite}</span>
      ) : (
        <span className="text-mdx-13 text-mdx-slate">{ALLOWLIST_HELP}</span>
      )}
      {problem ? <Callout>{problem}</Callout> : null}
    </div>
  )
}

/* ── "Email it to a developer" — DECISION-023 ───────────────────────────── */

/**
 * A PICKER, never a text field.
 *
 * DECISION-023 and RULE-067: "the recipient is picked from the org member list,
 * never a free-text address". There is no input here to type an address into,
 * which is the point — the rule is a property of the control rather than a
 * validation on it.
 */
function EmailToDeveloper({
  workspaceId,
  funnelId,
  mode,
  orgId,
}: {
  workspaceId: string | null
  funnelId: string
  mode: EmbedMode
  orgId: string | null
}) {
  const [open, setOpen] = useState(false)
  const [people, setPeople] = useState<{ userId: string; name: string; email: string }[] | null>(null)
  const [chosen, setChosen] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [problem, setProblem] = useState<string | null>(null)

  const openPicker = () => {
    setOpen(true)
    setProblem(null)
    if (people || !orgId) return
    void (async () => {
      try {
        const view = await apiGet<{ people: { userId: string; name: string; email: string }[] }>(
          `/v1/orgs/${orgId}/members`,
        )
        setPeople(view.people)
        setChosen(view.people[0]?.userId ?? '')
      } catch (e) {
        setProblem(
          e instanceof ApiFailure ? e.message : "We couldn't load the people in this organisation.",
        )
      }
    })()
  }

  const send = async () => {
    if (!workspaceId || !chosen) return
    setState('sending')
    setProblem(null)
    try {
      await apiPost(`/v1/w/${workspaceId}/funnels/${funnelId}/embed/email`, {
        member_user_id: chosen,
        mode,
      })
      setState('sent')
    } catch (e) {
      setState('idle')
      setProblem(e instanceof ApiFailure ? e.message : "We couldn't send that. Try again.")
    }
  }

  if (!open) {
    return (
      <Button variant="secondary" onClick={openPicker}>
        Email it to a developer
      </Button>
    )
  }

  return (
    <div className="flex w-full flex-col gap-mdx-8">
      <div className="flex flex-wrap items-center gap-mdx-8">
        <select
          aria-label="Who to send it to"
          value={chosen}
          onChange={(event) => setChosen(event.target.value)}
          disabled={!people}
          className="h-mdx-input min-w-0 flex-1 cursor-pointer rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-14 text-mdx-15 text-mdx-ink outline-none focus:border-mdx-brand disabled:cursor-not-allowed disabled:opacity-mdx-recessed"
        >
          {people ? null : <option value="">Loading the people in this organisation…</option>}
          {(people ?? []).map((person) => (
            <option key={person.userId} value={person.userId}>
              {person.name} — {person.email}
            </option>
          ))}
        </select>
        <Button disabled={!chosen || state !== 'idle'} onClick={() => void send()}>
          {state === 'sent' ? 'Sent' : state === 'sending' ? 'Sending' : 'Send'}
        </Button>
        <Button variant="secondary" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      <span className="text-mdx-13 text-mdx-slate">
        Only people already in this organisation can be sent the code.
      </span>
      {problem ? <Callout>{problem}</Callout> : null}
    </div>
  )
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex justify-center px-mdx-24 pb-mdx-48 pt-mdx-28">
      <div className="flex w-full max-w-mdx-measure flex-col gap-mdx-24">{children}</div>
    </div>
  )
}
